import { Link, Navigate, useParams } from 'react-router-dom'
import {ArrowLeft, Calendar, Clock, User} from 'lucide-react'
import Layout from '@/components/Layout'
import Container from '@/components/ui/Container'
import SectionHeading from '@/components/ui/SectionHeading'
import NewsletterForm from '@/components/NewsletterForm'
import { blogPosts } from '@/data/blogPosts'

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })
}

function BlogDetail() {
  const { slug } = useParams<{ slug: string }>()
  const post = blogPosts.find(p => p.slug === slug)

  if (!post)
    return <Navigate to="/blog" replace />

  const relatedPosts = blogPosts.filter(p => p.slug !== post.slug).slice(0, 3)

  return (
    <Layout>
      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0">
          <img src={post.image} alt={post.title} className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/60" />
        </div>
        <Container className="relative py-20 sm:py-24">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 hover:text-white">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Voltar para o Blog
          </Link>
          <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-xs font-semibold text-cyan-300">
            {post.category}
          </span>
          <h1 className="mt-5 max-w-3xl text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-300">
            <span className="flex items-center gap-1.5"><User className="h-4 w-4" aria-hidden="true" /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" aria-hidden="true" /> {formatDate(post.date)}</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" aria-hidden="true" /> {post.readTime} de leitura</span>
          </div>
        </Container>
      </section>

      <section className="py-16 sm:py-20">
        <Container className="max-w-3xl">
          <p className="text-lg leading-relaxed text-slate-700">{post.content}</p>
        </Container>
      </section>

      <section className="bg-slate-50 py-20 sm:py-24">
        <Container>
          <SectionHeading
            eyebrow="Continue lendo"
            title="Outros artigos que podem te interessar"
          />
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {relatedPosts.map(related => (
              <Link
                key={related.id}
                to={`/blog/${related.slug}`}
                className="group overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-shadow hover:shadow-lg"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={related.image}
                    alt={related.title}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <span className="text-xs font-semibold uppercase tracking-wide text-cyan-600">{related.category}</span>
                  <h3 className="mt-2 text-base font-semibold text-slate-900">{related.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 sm:py-20">
        <Container className="flex flex-col items-center gap-6 text-center">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Receba nossas novidades</h2>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-slate-600">
              Conteúdos exclusivos sobre segurança eletrônica e conectividade corporativa, direto no seu e-mail.
            </p>
          </div>
          <div className="w-full max-w-md rounded-2xl bg-slate-900 p-4">
            <NewsletterForm />
          </div>
        </Container>
      </section>
    </Layout>
  )
}

export default BlogDetail
